import React from 'react';
import { Box, Chip, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { HelpOutline, Logout } from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { COLORS } from '../../theme';
import { getEffectiveUserId } from '../../utils/auth';
import { getUserNavItems } from './userNavigation';
import UserAvatarButton from './UserAvatarButton';
import ThemeToggle from '../ThemeToggle';
import NotificationBell from '../notificacoes/NotificationBell';
import logoBranco from '../../assets/logos/Logo Branco com Laranja.svg';
import { Diamond, GridOverlay, Sparkle } from '../landing/LandingShapes';
import { encerrarSessao } from '../../utils/logout';

const UserSidebarBrand = ({ usuario, onAvatarUpdated, canEditAvatar = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const userId = getEffectiveUserId();
  const items = getUserNavItems(userId);
  const handleLogout = async () => {
    await encerrarSessao();
    navigate('/');
  };

  return (
    <Box
      component="aside"
      sx={{
        width: 280,
        position: 'fixed',
        left: 0,
        top: 0,
        bottom: 0,
        zIndex: 1200,
        bgcolor: COLORS.dark,
        color: COLORS.cream,
        display: { xs: 'none', md: 'flex' },
        flexDirection: 'column',
        borderRight: '1px solid rgba(242,238,231,0.12)',
        overflow: 'hidden',
        p: { md: 2.6 },
      }}
    >
      <GridOverlay dark />
      <Sparkle
        size={40}
        color={COLORS.orange}
        sx={{ position: 'absolute', top: 26, right: 24, opacity: 0.5 }}
      />
      <Diamond
        size={12}
        color={COLORS.orange}
        sx={{ position: 'absolute', bottom: 150, right: 36, opacity: 0.5 }}
      />

      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        }}
      >
        <Box
          component="button"
          type="button"
          onClick={() => navigate('/user/dashboard')}
          aria-label="Ir para o painel"
          sx={{
            border: 0,
            bgcolor: 'transparent',
            p: 0,
            cursor: 'pointer',
            display: 'flex',
            justifyContent: 'flex-start',
            mb: 3.5,
          }}
        >
          <Box component="img" src={logoBranco} alt="Zoompro Studio" sx={{ height: 32 }} />
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.4,
            p: 1.6,
            borderRadius: '18px',
            bgcolor: 'rgba(242,238,231,0.06)',
            border: '1px solid rgba(242,238,231,0.1)',
            mb: 2.5,
          }}
        >
          <UserAvatarButton
            usuario={usuario}
            size={46}
            badgeSize={20}
            badgeBorderColor={COLORS.dark}
            editable={canEditAvatar}
            onAvatarUpdated={onAvatarUpdated}
          />
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontFamily: '"Poppins", sans-serif',
                fontWeight: 800,
                fontSize: '0.9rem',
                color: COLORS.cream,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {usuario?.nome || 'Minha conta'}
            </Typography>
            <Typography
              sx={{
                color: 'rgba(242,238,231,0.55)',
                fontSize: '0.72rem',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {usuario?.email || 'Área do cliente'}
            </Typography>
          </Box>
        </Box>

        <Box
          data-tour="user-nav"
          sx={{ position: 'relative', zIndex: 1, display: 'grid', gap: 0.5 }}
        >
          {items.map(({ icon: Icon, text, path, tour }) => {
            const active =
              location.pathname === path ||
              (path === '/user/orders' && location.pathname.startsWith('/user/pedido/'));
            return (
              <Box
                key={path}
                component="button"
                type="button"
                data-tour={tour}
                onClick={() => navigate(path)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.3,
                  border: 0,
                  cursor: 'pointer',
                  textAlign: 'left',
                  width: '100%',
                  px: 1.4,
                  py: 1.1,
                  borderRadius: '14px',
                  bgcolor: active ? COLORS.orange : 'transparent',
                  color: active ? COLORS.white : 'rgba(242,238,231,0.78)',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: active ? COLORS.orange : 'rgba(242,238,231,0.08)',
                    color: COLORS.white,
                  },
                }}
              >
                <Icon sx={{ fontSize: 20 }} />
                <Typography sx={{ fontWeight: active ? 800 : 600, fontSize: '0.88rem' }}>
                  {text}
                </Typography>
              </Box>
            );
          })}
        </Box>

        <Box sx={{ mt: 'auto', position: 'relative', zIndex: 1 }}>
          <Box
            component="button"
            type="button"
            onClick={() => window.dispatchEvent(new CustomEvent('zoompro:open-help'))}
            sx={{
              width: '100%',
              border: '1px dashed rgba(242,238,231,0.28)',
              bgcolor: 'rgba(242,238,231,0.04)',
              color: COLORS.cream,
              cursor: 'pointer',
              borderRadius: '14px',
              px: 1.4,
              py: 1.1,
              display: 'flex',
              alignItems: 'center',
              gap: 1.1,
              mb: 1.4,
              '&:hover': { borderColor: COLORS.orange, bgcolor: 'rgba(235,91,37,0.1)' },
            }}
          >
            <HelpOutline sx={{ fontSize: 19, color: COLORS.orange }} />
            <Typography sx={{ fontWeight: 700, fontSize: '0.8rem' }}>
              Como usar a plataforma
            </Typography>
          </Box>

          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" alignItems="center" spacing={0.25}>
              <Box
                sx={{
                  '& .MuiIconButton-root': {
                    color: 'rgba(242,238,231,0.8) !important',
                    '&:hover': {
                      bgcolor: 'rgba(242,238,231,0.08) !important',
                      color: `${COLORS.orange} !important`,
                    },
                  },
                }}
              >
                <ThemeToggle />
              </Box>
              <Box
                sx={{
                  '& .MuiIconButton-root': {
                    color: 'rgba(242,238,231,0.8) !important',
                    '&:hover': {
                      bgcolor: 'rgba(242,238,231,0.08) !important',
                      color: `${COLORS.orange} !important`,
                    },
                  },
                }}
              >
                <NotificationBell color={COLORS.cream} centralPath="/user/notificacoes" />
              </Box>
            </Stack>
            <Tooltip title="Sair da conta" arrow>
              <IconButton
                onClick={handleLogout}
                sx={{
                  color: COLORS.orange,
                  bgcolor: 'rgba(235,91,37,0.12)',
                  '&:hover': { bgcolor: 'rgba(235,91,37,0.22)' },
                }}
              >
                <Logout fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>

          <Chip
            label="Zoompro Studio"
            size="small"
            sx={{
              mt: 1.6,
              bgcolor: 'rgba(242,238,231,0.06)',
              color: 'rgba(242,238,231,0.55)',
              fontSize: '0.65rem',
              letterSpacing: 0.5,
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default UserSidebarBrand;
